import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  ArrowLeft,
  Truck,
  MapPin,
  Calendar,
  User,
  Package,
  Loader2,
  Paperclip,
  Download,
  Pencil,
} from 'lucide-react'
import { useDelivery } from '@/hooks/queries/useDeliveries'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export function DeliveryDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const deliveryId = id ? parseInt(id, 10) : 0

  const { data: delivery, isLoading } = useDelivery(deliveryId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    )
  }

  if (!delivery) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <p className="text-gray-500">Entrega no encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </div>
    )
  }

  const deliveredDate = new Date(delivery.delivered_at)
  const totalKilos = delivery.items.reduce((sum, i) => sum + Number(i.kilos), 0)

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4">
      {/* Encabezado */}
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Truck className="h-6 w-6" />
            Entrega #{delivery.id}
          </h1>
          <p className="text-sm text-muted-foreground">
            {format(deliveredDate, "EEEE d 'de' MMMM yyyy, HH:mm", { locale: es })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
          onClick={() => navigate(`/deliveries/${delivery.id}/edit`)}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Editar
        </Button>
      </div>

      {/* Destino y operador */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-green-600" />
            Destino
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="font-medium">
              {delivery.destination?.name ?? `Destino #${delivery.destination_id}`}
            </p>
            {delivery.destination && (
              <p className="text-muted-foreground">
                {[
                  delivery.destination.is_collection_point && 'Punto de acopio',
                  delivery.destination.is_valorizer && 'Valorizador',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Fecha de entrega
              </p>
              <p className="font-medium">
                {format(deliveredDate, "d 'de' MMMM yyyy, HH:mm", { locale: es })}
              </p>
            </div>
            {delivery.operator_id && (
              <div>
                <p className="text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Operador
                </p>
                <p className="font-medium">Operador #{delivery.operator_id}</p>
              </div>
            )}
          </div>
          {delivery.notes && (
            <div>
              <p className="text-muted-foreground mb-1">Observaciones</p>
              <p className="rounded-md bg-gray-50 p-3">{delivery.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ítems entregados */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Package className="h-4 w-4 text-green-600" />
            Ítems entregados · {totalKilos} kg total
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {delivery.items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">
                  {item.pickupRecordItem?.waste_type?.name ?? `Ítem #${item.pickup_record_item_id}`}
                </p>
                {item.pickupRecordItem?.pickupRecord?.pickup?.client && (
                  <p className="text-xs text-muted-foreground">
                    {item.pickupRecordItem.pickupRecord.pickup.client.trade_name}
                  </p>
                )}
              </div>
              <span className="ml-3 shrink-0 font-semibold">{item.kilos} kg</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Documento de respaldo */}
      {delivery.document && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Paperclip className="h-4 w-4 text-green-600" />
              Documento de respaldo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{delivery.document.name}</p>
                <p className="text-xs text-muted-foreground">{delivery.document.mime}</p>
              </div>
              {delivery.document.url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={delivery.document.url} target="_blank" rel="noopener noreferrer">
                    <Download className="mr-2 h-4 w-4" />
                    Descargar
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
